"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "radix-ui";
import { Button } from "../ui/button";

export function requestWorkspaceLeave(leave: () => void) {
  const event = new CustomEvent("workspace:before-leave", { cancelable: true, detail: { leave } });
  if (document.dispatchEvent(event)) leave();
}

/** 在外壳首次挂载时注册，先于路由的后退处理询问当前编辑页。 */
export function useWorkspaceHistoryGuard() {
  useEffect(() => {
    const beforeTraverse = (event: PopStateEvent) => {
      const request = new Event("workspace:history-traverse", { cancelable: true });
      if (!document.dispatchEvent(request)) event.stopImmediatePropagation();
    };
    window.addEventListener("popstate", beforeTraverse);
    return () => window.removeEventListener("popstate", beforeTraverse);
  }, []);
}

/** 内容只留在当前页面内存中；退出前告知，不把敏感草稿写入浏览器持久存储。 */
export function useUnsavedWork(dirty: boolean, busy = false) {
  const [pending, setPending] = useState<{ leave: () => void } | null>(null);
  const proceeding = useRef(false);
  const focus = useRef<HTMLElement | null>(null);
  const requestLeave = useCallback(
    (leave: () => void) => {
      if (!dirty && !busy) {
        leave();
        return;
      }
      focus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setPending({ leave });
    },
    [dirty, busy]
  );

  useEffect(() => {
    if (!dirty && !busy) {
      proceeding.current = false;
      return;
    }
    const currentUrl = window.location.href;
    const currentState = window.history.state;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (proceeding.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const onLink = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const link =
        event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
      const url = new URL(link.href, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return;
      if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      requestLeave(() => window.location.assign(url.href));
    };
    const onAction = (event: Event) => {
      event.preventDefault();
      requestLeave((event as CustomEvent<{ leave: () => void }>).detail.leave);
    };
    const onHistory = (event: Event) => {
      if (proceeding.current) return;
      const previous = new URL(currentUrl);
      if (location.pathname === previous.pathname && location.search === previous.search) return;
      // popstate 不可取消。先恢复当前编辑页，再让用户决定是否回到刚刚的目标；
      // 外壳先询问当前编辑页，字段值继续留在本页内存。
      event.preventDefault();
      history.pushState(currentState, "", currentUrl);
      requestLeave(() => history.back());
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("workspace:history-traverse", onHistory);
    document.addEventListener("click", onLink, true);
    document.addEventListener("workspace:before-leave", onAction);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("workspace:history-traverse", onHistory);
      document.removeEventListener("click", onLink, true);
      document.removeEventListener("workspace:before-leave", onAction);
    };
  }, [dirty, busy, requestLeave]);

  const dialog = (
    <Dialog.Root
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) setPending(null);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="discard-overlay" />
        <Dialog.Content
          className="discard-dialog workspace"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!proceeding.current) focus.current?.focus();
          }}
        >
          <Dialog.Title>{busy ? "正在保存内容" : "还有未保存的内容"}</Dialog.Title>
          <Dialog.Description>
            {busy
              ? "请等待保存结果，再离开当前页面。"
              : "离开后，本次未保存的内容将丢失。你可以继续填写并保存。"}
          </Dialog.Description>
          <div className="workspace-actions">
            <Button type="button" variant="outline" onClick={() => setPending(null)}>
              {busy ? "留在当前页" : "继续填写"}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={() => {
                proceeding.current = true;
                pending?.leave();
                setPending(null);
              }}
            >
              放弃并离开
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
  return { requestLeave, dialog };
}
